/**
 * @fileoverview The cards on Gardens on Grow (/gardens): a listed garden and a
 * plant found by the search, as the owner's approved mock draws them.
 *
 * - GardenRow: a garden as a card with its cover photo at the left (a leaf
 *   tile without one), "Amy's garden" in the page's serif, "@amy",
 *   "22 plants · 4 beds" and how long ago it was updated. The whole card opens
 *   the garden's page (/@amy).
 * - PlantResult: a found plant as a card with its newest public photo (a leaf
 *   tile without one) and its stage as a chip, its one name ("Marketmore 76
 *   Cucumber", the backend's names), and the garden it grows in ("@amy"). It opens the plant's own page
 *   (/@amy/marketmore-76), or the garden's when the plant has no address yet.
 *
 * Only what the backend sends is drawn, and it sends only what a garden's own
 * public page already shows. Images are lazy-loaded at fixed shapes, so nothing
 * jumps as they arrive.
 *
 * Where: GardensPage.
 * API: none (the page's data is gardens getGardenDirectory / searchDirectoryPlants).
 * Settings: none.
 * Words (en/es): gardenPage.page.titleOf, .title, .seedPacket,
 * .count.*, .noPhoto, gardensPage.updated.*.
 *
 * @module gardensPageParts
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Sprout } from 'lucide-react';
import { Pill } from '@/shared/components/ui/Pill';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { gardenItemPathFor, type DirectoryGarden, type DirectoryPlant } from '@/shared/api/gardens';
import { apiSrc, updatedWords } from './lib/gardenPage';
import { titleName } from './gardenPageSections';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';

/**
 * The line "@amy": the gardener's address with its "@".
 *
 * @param slug - The garden's address, without its "@".
 * @returns The line.
 */
function handleLine(slug: string): string {
  return `@${slug}`;
}

/**
 * A photo, or the quiet leaf tile in its place when there is none or it
 * will not load.
 *
 * @param props.src - The photo's API path, or null.
 * @param props.className - The picture's shape and size.
 */
const Picture: React.FC<{ src: string | null; className: string }> = ({ src, className }) => {
  const { reactTranslate: t } = useTranslation();
  const [broken, setBroken] = React.useState(false);
  if (src && !broken) {
    return (
      <span className={`${className} relative block overflow-hidden bg-muted data-[small=true]:bg-transparent`}>
        <FramedPhoto src={apiSrc(src)} alt="" loading="lazy" draggable={false} onError={() => setBroken(true)} />
      </span>
    );
  }
  return (
    <span className={`${className} flex items-center justify-center bg-muted`} role="img" aria-label={t('gardenPage.page.noPhoto')}>
      <Sprout className="h-10 w-10 text-primary/35" aria-hidden="true" />
    </span>
  );
};

/**
 * A listed garden as a card that opens its page.
 *
 * @param props.garden - The garden as the listing sent it.
 */
export const GardenRow: React.FC<{ garden: DirectoryGarden }> = ({ garden }) => {
  const { reactTranslate: t } = useTranslation();
  const shown = titleName(garden.name);
  const title = shown ? t('gardenPage.page.titleOf', { name: shown }) : t('gardenPage.page.title');
  const handle = handleLine(garden.slug);
  const counts = [
    garden.plants > 0 ? t(garden.plants === 1 ? 'gardenPage.page.count.plants_one' : 'gardenPage.page.count.plants', { count: garden.plants }) : null,
    garden.beds > 0 ? t(garden.beds === 1 ? 'gardenPage.page.count.beds_one' : 'gardenPage.page.count.beds', { count: garden.beds }) : null,
  ].filter(Boolean).join(' · ');
  const updated = updatedWords(garden.updated, t);
  return (
    // min-w-0: a grid item never grows past its column, so a long name wraps instead of pushing the card off the phone.
    <li className="min-w-0">
      <Link to={`/@${garden.slug}`} className="block overflow-hidden rounded-2xl border bg-card hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {/*
          * Its plants' photos on top as a collage: the cover large, then up to four
          * more beside it (three with four photos, two with three); just the cover when that is all there is.
          */}
        {garden.photos.length >= 5 ? (
          <span className="grid aspect-[16/9] w-full grid-cols-[2fr_1fr_1fr] grid-rows-2 gap-0.5">
            <Picture src={garden.photos![0].src} className="row-span-2 h-full w-full" />
            {garden.photos!.slice(1, 5).map((photo) => <Picture key={photo.src} src={photo.src} className="h-full w-full" />)}
          </span>
        ) : garden.photos.length === 4 ? (
          <span className="grid aspect-[16/9] w-full grid-cols-[2fr_1fr] grid-rows-3 gap-0.5">
            <Picture src={garden.photos![0].src} className="row-span-3 h-full w-full" />
            {garden.photos!.slice(1, 4).map((photo) => <Picture key={photo.src} src={photo.src} className="h-full w-full" />)}
          </span>
        ) : garden.photos.length >= 3 ? (
          <span className="grid aspect-[16/9] w-full grid-cols-[2fr_1fr] grid-rows-2 gap-0.5">
            <Picture src={garden.photos![0].src} className="row-span-2 h-full w-full" />
            <Picture src={garden.photos![1].src} className="h-full w-full" />
            <Picture src={garden.photos![2].src} className="h-full w-full" />
          </span>
        ) : (
          <Picture src={garden.photos[0]?.src ?? garden.cover?.src ?? null} className="aspect-[16/9] w-full" />
        )}
        <span className="block space-y-1 px-4 py-3">
          <span className="block font-grow text-lg font-bold leading-snug [overflow-wrap:anywhere]">{title}</span>
          {/* The address once: only when the title does not already say it. */}
          {handle && !String(title).includes(handle.replace(/^@/, '')) && <span className="block truncate text-sm text-muted-foreground">{handle}</span>}
          <span className="block text-sm text-muted-foreground">{[counts, updated].filter(Boolean).join(' · ')}</span>
        </span>
      </Link>
    </li>
  );
};

/**
 * A found plant as a card that opens its page.
 *
 * @param props.plant - The plant as the search sent it.
 */
export const PlantResult: React.FC<{ plant: DirectoryPlant }> = ({ plant }) => {
  const { reactTranslate: t } = useTranslation();
  const to = plant.slug ? gardenItemPathFor(plant.garden.slug, plant.slug) : `/@${plant.garden.slug}`;
  return (
    <li className="min-w-0">
      <Link to={to} className="block overflow-hidden rounded-2xl border bg-card hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <span className="relative block">
          <Picture src={plant.photo?.src ?? null} className="aspect-square w-full" />
          {plant.stage && (
            <Pill size="sm" tone="photoLight" className="absolute left-2 top-2">{plant.stage.name}</Pill>
          )}
          {plant.photo?.packet && (
            <Pill size="sm" tone="photo" className="absolute bottom-2 left-2">{t('gardenPage.page.seedPacket')}</Pill>
          )}
        </span>
        <span className="block px-3 py-3">
          {/* The plant's one name (the backend's names, services/grow/plantNaming); its kind is not said under it. */}
          <span className="block truncate font-grow text-base font-bold">{plant.names.full}</span>
          <span className="block truncate text-sm text-muted-foreground">{handleLine(plant.garden.slug)}</span>
        </span>
      </Link>
    </li>
  );
};
